import { type ReactNode, useEffect, useId, useState } from "react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { describeCatalogError } from "../errors";

// A controlled lookup: a search box, a short list of matches, and one "Add" button per match. A match
// that is already chosen is left out. The search runs when the staff member stops typing (200 ms) and
// the last request wins, so a slow answer never replaces a newer one. "Create new" is offered next to
// the box when the lookup finds nothing it wants.

type LookupAddProps<T> = Readonly<{
  label: string;
  /** Runs the search. `signal` aborts a request that a newer search replaced. */
  search: (query: string, signal: AbortSignal) => Promise<readonly T[]>;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  describe?: (item: T) => string | null;
  isChosen: (item: T) => boolean;
  onPick: (item: T) => void;
  disabled?: boolean;
  /** Extra control shown before the search box (for example a tag namespace select). */
  before?: ReactNode;
  /** Bumps when the search should run again although the text did not change (a new namespace). */
  searchKey?: string;
  onCreate?: () => void;
  createLabel?: string;
}>;

const DEBOUNCE_MS = 200;

export function LookupAdd<T>({
  label,
  search,
  getKey,
  getLabel,
  describe,
  isChosen,
  onPick,
  disabled = false,
  before,
  searchKey = "",
  onCreate,
  createLabel,
}: LookupAddProps<T>) {
  const inputId = useId();
  const resultsId = useId();
  const [text, setText] = useState("");
  const [results, setResults] = useState<readonly T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `search` is rebuilt on every render of the caller; `searchKey` and `text` are the real triggers
  useEffect(() => {
    if (disabled) return;
    const controller = new AbortController();
    const timer = setTimeout(
      () => {
        setSearching(true);
        search(text.trim(), controller.signal)
          .then((found) => {
            if (controller.signal.aborted) return;
            setResults(found);
            setError(null);
          })
          .catch((cause: unknown) => {
            if (controller.signal.aborted) return;
            setError(describeCatalogError(cause).message);
          })
          .finally(() => {
            if (!controller.signal.aborted) setSearching(false);
          });
      },
      text === "" ? 0 : DEBOUNCE_MS,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [text, searchKey, disabled]);

  const visible = (results ?? []).filter((item) => !isChosen(item));

  return (
    <div className="grid gap-2">
      <div className="grid items-end gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,13.75rem),1fr))]">
        {before}
        <div className="grid min-w-0 content-start gap-1.5">
          <Label htmlFor={inputId}>{label}</Label>
          <Input
            id={inputId}
            type="search"
            value={text}
            disabled={disabled}
            autoComplete="off"
            aria-controls={resultsId}
            onChange={(event) => setText(event.currentTarget.value)}
          />
        </div>
        {onCreate ? (
          <Button type="button" variant="secondary" onClick={onCreate} disabled={disabled}>
            {createLabel ?? "Create new"}
          </Button>
        ) : null}
      </div>
      <div id={resultsId} className="grid gap-2">
        <p role="status" className="m-0 text-sm leading-5 text-muted-foreground">
          {error !== null
            ? error
            : searching
              ? "Searching…"
              : results === null
                ? ""
                : visible.length === 0
                  ? "No matches."
                  : `${visible.length} ${visible.length === 1 ? "match" : "matches"}.`}
        </p>
        {visible.length > 0 ? (
          <ul className="m-0 grid list-none gap-2 p-0">
            {visible.slice(0, 10).map((item, position) => {
              const nameId = `${resultsId}-name-${position}`;
              const buttonId = `${resultsId}-add-${position}`;
              return (
                <li
                  key={getKey(item)}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-border px-3 py-2"
                >
                  <span>
                    <span id={nameId}>{getLabel(item)}</span>
                    {describe?.(item) ? (
                      <span className="text-sm text-muted-foreground"> {describe(item)}</span>
                    ) : null}
                  </span>
                  {/* The name is "Add <the name above>": the page text, not an attribute, carries what the person typed. */}
                  <Button
                    id={buttonId}
                    type="button"
                    variant="secondary"
                    disabled={disabled}
                    aria-labelledby={`${buttonId} ${nameId}`}
                    onClick={() => onPick(item)}
                  >
                    Add
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
